import { useEffect, useRef, useState } from 'react';
import { AccessibilityInfo, Animated, Easing, Keyboard, Platform, Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import usePlatform from '@/utils/hooks/usePlatform';
import Unicon from './Unicon';
import CelebrationConfetti from '@/components/Milestones/CelebrationConfetti';
import CutCard from './CutCard';

export function useAchievementReducedMotion() {
    const [reduced, setReduced] = useState(null);
    useEffect(() => {
        if (Platform.OS === 'web') {
            const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
            setReduced(media?.matches === true);
            const change = event => setReduced(event.matches);
            media?.addEventListener?.('change', change);
            return () => media?.removeEventListener?.('change', change);
        }
        let mounted = true;
        AccessibilityInfo.isReduceMotionEnabled().then(value => { if (mounted) setReduced(value); })
            .catch(() => { if (mounted) setReduced(true); });
        const listener = AccessibilityInfo.addEventListener('reduceMotionChanged', setReduced);
        return () => { mounted = false; listener.remove(); };
    }, []);
    return reduced;
}

export default function AchievementToast({
    title, message, items = [], onDismiss, onAction, actionLabel = 'View achievements',
    reducedMotion: reducedOverride, testID = 'achievement-toast',
    dismissTestID = 'achievement-toast-dismiss', actionTestID = 'achievement-toast-action',
    duration = 6500,
}) {
    const reducedPreference = useAchievementReducedMotion();
    const reduced = reducedPreference === null ? null : reducedPreference || reducedOverride === true;
    const insets = useSafeAreaInsets();
    const { isLandscape } = usePlatform();
    const { height } = useWindowDimensions();
    const [keyboardHeight, setKeyboardHeight] = useState(0);
    const [burst, setBurst] = useState(false);
    const [confettiFailed, setConfettiFailed] = useState(false);
    const [paused, setPaused] = useState(false);
    const entrance = useRef(new Animated.Value(0)).current;
    const pop = useRef(new Animated.Value(1)).current;
    const check = useRef(new Animated.Value(0)).current;
    const closing = useRef(false);
    const dismissRef = useRef(onDismiss);
    dismissRef.current = onDismiss;

    useEffect(() => {
        const show = Keyboard.addListener('keyboardDidShow', event => setKeyboardHeight(Math.max(0, height - event.endCoordinates.screenY)));
        const hide = Keyboard.addListener('keyboardDidHide', () => setKeyboardHeight(0));
        return () => { show.remove(); hide.remove(); };
    }, [height]);

    useEffect(() => {
        if (reduced === null) return;
        if (reduced) {
            entrance.setValue(1);
            check.setValue(1);
            pop.setValue(1);
            setBurst(false);
            return;
        }
        check.setValue(0);
        const animation = Animated.sequence([
            Animated.timing(entrance, { toValue: 1, duration: 220, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
            Animated.parallel([
                Animated.spring(check, { toValue: 1, friction: 5, tension: 110, useNativeDriver: true }),
                Animated.sequence([
                    Animated.timing(pop, { toValue: 1.035, duration: 110, useNativeDriver: true }),
                    Animated.spring(pop, { toValue: 1, friction: 5, tension: 120, useNativeDriver: true }),
                ]),
            ]),
        ]);
        const timer = setTimeout(() => setBurst(true), 220);
        animation.start();
        return () => { clearTimeout(timer); animation.stop(); };
    }, [reduced, entrance, pop, check]);

    const dismiss = () => {
        if (closing.current) return;
        closing.current = true;
        Animated.timing(entrance, { toValue: 0, duration: reduced ? 0 : 180, useNativeDriver: true })
            .start(({ finished }) => { if (finished) dismissRef.current?.(); });
    };
    const dismissAction = useRef(dismiss);
    dismissAction.current = dismiss;
    useEffect(() => {
        if (paused || duration === 0) return;
        const timer = setTimeout(() => dismissAction.current(), duration);
        return () => clearTimeout(timer);
    }, [paused, duration]);
    useEffect(() => {
        if (Platform.OS !== 'web') return;
        const key = event => {
            if (event.key === 'Escape' && !event.defaultPrevented) dismissAction.current();
        };
        document.addEventListener('keydown', key);
        return () => document.removeEventListener('keydown', key);
    }, []);

    if (reduced === null) return null;
    const bottom = Math.max(keyboardHeight + 28, isLandscape ? insets.bottom + 24 : insets.bottom + 100);
    return (
        <View pointerEvents="box-none" style={[styles.position, { bottom }]} testID={`${testID}-position`}>
            <Animated.View
                testID={testID}
                accessibilityLiveRegion="polite"
                style={[styles.card, { opacity: entrance, transform: [
                    { translateY: entrance.interpolate({ inputRange: [0, 1], outputRange: [14, 0] }) },
                    { scale: pop },
                ] }]}
                onPointerEnter={() => setPaused(true)}
                onPointerLeave={() => setPaused(false)}
                onFocus={() => setPaused(true)}
                onBlur={() => setPaused(false)}
            >
                <CutCard testID={`${testID}-card`} accentColor="success" contentStyle={styles.cardContent}>
                <View style={styles.content}>
                    <Animated.View style={[styles.badge, { transform: [{ scale: check }] }]}>
                        <Unicon name="checkmark" size={25} colorVariant="success" />
                    </Animated.View>
                    <View style={styles.copy}>
                        <Text style={styles.eyebrow}>A step forward</Text>
                        <Text selectable style={styles.title}>{title}</Text>
                        {!!message && <Text selectable style={styles.message}>{message}</Text>}
                    </View>
                    <Pressable testID={dismissTestID} accessibilityRole="button" accessibilityLabel="Dismiss achievement"
                        hitSlop={8} style={styles.close} onPress={dismiss}>
                        <Unicon name="close" size={20} colorVariant="textSecondary" />
                    </Pressable>
                </View>
                {items.length > 1 && <ScrollView style={styles.details} keyboardShouldPersistTaps="handled">
                    {items.map((item, index) => <View key={item.receiptID ?? item.awardID ?? index} style={styles.detail}>
                        <Unicon name={item.badgeIcon || 'ribbon-outline'} size={20} colorVariant={item.badgeColorToken || 'primary'} />
                        <View style={styles.copy}>
                            <Text style={styles.detailTitle}>{item.name}</Text>
                            <Text style={styles.message}>{item.title}</Text>
                            {!!item.message && <Text style={styles.message}>{item.message}</Text>}
                        </View>
                    </View>)}
                </ScrollView>}
                {!!onAction && <Pressable testID={actionTestID} accessibilityRole="button" style={styles.action}
                    onPress={() => { if (!closing.current) { closing.current = true; onAction(); } }}>
                    <Text style={styles.actionText}>{actionLabel}</Text>
                    <Unicon name="arrow-forward" size={16} colorVariant="primary" />
                </Pressable>}
                {burst && !reduced && !confettiFailed && <View pointerEvents="none" style={styles.confetti}>
                    <CelebrationConfetti style={styles.confettiAnimation} onFailure={() => setConfettiFailed(true)} />
                </View>}
                </CutCard>
            </Animated.View>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, typography }) => ({
    position: { position: 'absolute', right: spacing.md, left: spacing.md, alignItems: 'flex-end', zIndex: 1100 },
    card: { width: '100%', maxWidth: 390 },
    cardContent: { padding: spacing.md, gap: spacing.sm },
    content: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    badge: { width: 44, height: 44, borderRadius: 3, backgroundColor: colors.successContainer, alignItems: 'center', justifyContent: 'center' },
    copy: { flex: 1, minWidth: 0, gap: 3 },
    eyebrow: { fontFamily: fonts.bold, fontSize: typography.caption, color: colors.success },
    title: { fontFamily: fonts.bold, fontSize: typography.body, color: colors.text },
    message: { fontFamily: fonts.body, fontSize: typography.caption, lineHeight: 18, color: colors.textSecondary },
    close: { padding: 6, alignSelf: 'flex-start', zIndex: 1 },
    details: { maxHeight: 180 },
    detail: { flexDirection: 'row', gap: spacing.sm, paddingVertical: spacing.sm },
    detailTitle: { fontFamily: fonts.bold, fontSize: typography.caption, color: colors.text },
    action: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', gap: spacing.xs, paddingVertical: spacing.xs, marginLeft: 44 + spacing.sm },
    actionText: { fontFamily: fonts.bold, fontSize: typography.caption, color: colors.primary },
    confetti: { ...StyleSheet.absoluteFillObject, overflow: 'hidden', borderRadius: 3 },
    confettiAnimation: { width: '100%', height: '100%' },
}));
